import React, { useMemo, useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Checkbox } from "@mui/material";
import Grid from '@mui/material/Grid2';

import { ModelBase } from "@models";
import { DataType } from "@dndModels/Types";

import DandDAPI from "@utilities/smart_api";
import { makeStyles } from "@utilities/theme_utilities";

import FilterBox from "@dndComponents/filtering/FilterBox";
import DisplayStringWithReplacementsRecursive from '@dndComponents/display/DisplayStringWithReplacementsRecursive';
import DandDButtonBox from './DandDButtonBoxSimple';

const api = DandDAPI.getInstance();

interface Props<T extends ModelBase> {
  data_type: DataType;
  objects: T[]; // Shown in the order given
  selected_ids: string[];
  onChange: (selected_ids: string[]) => void;
  disabled?: boolean;
  quick_fields?: string[];
  rows_per_page?: number;
  // Narrows what's shown after the FilterBox, for toggles the caller owns
  extra_filter?: (obj: T) => boolean;
  // Rendered between the FilterBox and the list, with what the FilterBox matched and what's shown
  header?: (filter_matches: T[], shown: T[]) => React.ReactNode;
  // Grey details under the name
  details?: (obj: T) => React.ReactNode;
  // What shows when a row is expanded, defaults to the description
  renderExpanded?: (obj: T) => React.ReactNode;
}

const useRowStyles = makeStyles()((theme) => {
  return ({
    root: {
      backgroundColor: theme.palette.secondary.main,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '4px',
      marginBottom: '4px',
    },
    expandedContent: {
      backgroundColor: '#000',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '4px',
      padding: '8px',
      marginTop: '8px',
    },
  });
});

// A filterable, paged list of objects with checkboxes for selecting them,
// where each row can be expanded to see more about it.
export default function CheckBoxIndex<T extends ModelBase>({
  data_type,
  objects,
  selected_ids,
  onChange,
  disabled = false,
  quick_fields,
  rows_per_page = 25,
  extra_filter,
  header,
  details,
  renderExpanded,
}: Props<T>) {
  const { classes: rowClasses } = useRowStyles();
  const [filters, setFilters] = useState<any>({});
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filter_matches = useMemo(() => api.filter(data_type, objects, filters) as T[], [data_type, objects, filters]);
  const shown = extra_filter ? filter_matches.filter(extra_filter) : filter_matches;
  const selected = new Set(selected_ids);

  const page_count = Math.max(Math.ceil(shown.length / rows_per_page), 1);
  // Selecting can shrink the list out from under the current page when extra_filter depends on it
  const use_page = Math.min(page, page_count - 1);
  const current_page = shown.slice(use_page * rows_per_page, (use_page + 1) * rows_per_page);

  const selectShown = (select: boolean) => {
    const new_set = new Set(selected);
    for (const obj of shown) {
      if (select) {
        new_set.add(obj._id);
      } else {
        new_set.delete(obj._id);
      }
    }
    onChange(Array.from(new_set));
  };

  const toggle = (id: string) => {
    if (selected.has(id)) {
      onChange(selected_ids.filter(o => o !== id));
    } else {
      onChange([...selected_ids, id]);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpanded(prev => {
      const new_set = new Set(prev);
      if (new_set.has(id)) {
        new_set.delete(id);
      } else {
        new_set.add(id);
      }
      return new_set;
    });
  };

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <FilterBox
          data_type={data_type}
          the_filter={filters}
          dont_use_api
          all_objects={objects}
          quick_fields={quick_fields}
          onChange={(new_filters: any) => {
            setFilters(new_filters);
            setPage(0);
          }}
        />
      </Grid>
      { header &&
        <Grid size={12}>
          { header(filter_matches, shown) }
        </Grid>
      }
      <Grid size={12} className="header">
        ({shown.length} shown, {selected_ids.length} selected)
      </Grid>
      <Grid size={4}>
        <DandDButtonBox
          name={`Select ${shown.length} Shown`}
          disabled={disabled}
          onClick={() => selectShown(true)}
        />
      </Grid>
      <Grid size={4}>
        <DandDButtonBox
          name={`Unselect ${shown.length} Shown`}
          disabled={disabled}
          onClick={() => selectShown(false)}
        />
      </Grid>
      <Grid size={4}>
        <DandDButtonBox
          name="Clear Selection"
          disabled={disabled || selected_ids.length === 0}
          onClick={() => onChange([])}
        />
      </Grid>
      <Grid size={12}>
        <Grid container spacing={0} direction="row">
          { current_page.map(obj => {
            const is_selected = selected.has(obj._id);
            const is_expanded = expanded.has(obj._id);
            return (
              <Grid key={obj._id} size={12} className={rowClasses.root}>
                <Grid container spacing={0} direction="row">
                  <Grid size={1} className="centered">
                    <Checkbox
                      checked={is_selected}
                      disabled={disabled}
                      onChange={() => toggle(obj._id)}
                    />
                  </Grid>
                  <Grid size={10}
                    className={is_selected ? "clickable selected" : "clickable"}
                    onClick={() => { if (!disabled) toggle(obj._id); }}>
                    <Grid container spacing={0} direction="row">
                      <Grid size={12} className="header">
                        { obj.name }
                      </Grid>
                      { details &&
                        <Grid size={12} className="small_label gray">
                          { details(obj) }
                        </Grid>
                      }
                    </Grid>
                  </Grid>
                  <Grid size={1} className="centered clickable" onClick={() => toggleExpanded(obj._id)}>
                    { is_expanded ? <ExpandLess /> : <ExpandMore /> }
                  </Grid>
                  { is_expanded &&
                    <Grid size={12} className={rowClasses.expandedContent}>
                      { renderExpanded ? renderExpanded(obj) :
                        <DisplayStringWithReplacementsRecursive display_string={obj.description} />
                      }
                    </Grid>
                  }
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      { current_page.length === 0 &&
        <Grid size={12} className="centered">
          Nothing found
        </Grid>
      }
      { page_count > 1 &&
        <Grid size={12}>
          <Grid container spacing={1}>
            <Grid size={2}>
              <DandDButtonBox
                name="Previous"
                disabled={use_page === 0}
                onClick={() => setPage(use_page - 1)}
              />
            </Grid>
            <Grid size={8} className="centered">
              Page { use_page + 1 } of { page_count }
            </Grid>
            <Grid size={2}>
              <DandDButtonBox
                name="Next"
                disabled={use_page + 1 >= page_count}
                onClick={() => setPage(use_page + 1)}
              />
            </Grid>
          </Grid>
        </Grid>
      }
    </Grid>
  );
}
